import { useEffect, useMemo, useRef, useState } from 'react'

import { useLatestCallback } from '@marketplace-web/shared/ui-state-util'

type UseTimerProgressBarParams = {
  duration: number
  isActive: boolean
  onComplete?: () => void
  reset: boolean
  totalCount: number
  currentIndex: number
  isManualMode: boolean
}

const ANIMATION_START_DELAY = 16

export const useTimerProgressBar = ({
  duration,
  isActive,
  onComplete,
  reset,
  totalCount,
  currentIndex,
  isManualMode,
}: UseTimerProgressBarParams) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasCompletedRef = useRef(false)
  const [animationKey, setAnimationKey] = useState(0)
  const [shouldStartAnimation, setShouldStartAnimation] = useState(false)

  const latestOnComplete = useLatestCallback(onComplete)

  const resetAnimationState = () => {
    hasCompletedRef.current = false
    setAnimationKey(prev => prev + 1)
    setShouldStartAnimation(false)
  }

  const clearTimers = () => {
    if (!timeoutRef.current) return

    clearTimeout(timeoutRef.current)
    timeoutRef.current = null
  }

  useEffect(() => {
    const startAnimation = () => {
      return setTimeout(() => {
        requestAnimationFrame(() => {
          setShouldStartAnimation(true)
        })
      }, ANIMATION_START_DELAY)
    }

    const startCompletionTimer = () => {
      return setTimeout(() => {
        if (hasCompletedRef.current) return

        hasCompletedRef.current = true
        latestOnComplete?.()
      }, duration + ANIMATION_START_DELAY)
    }

    resetAnimationState()
    clearTimers()

    if (isActive && !reset && !isManualMode) {
      const startDelay = startAnimation()
      timeoutRef.current = startCompletionTimer()

      return () => {
        clearTimeout(startDelay)
        clearTimers()
      }
    }

    return clearTimers
  }, [currentIndex, reset, isActive, isManualMode, duration, latestOnComplete])

  const progressSegments = useMemo(() => {
    return Array.from({ length: totalCount }, (_, index) => {
      if (isManualMode) {
        return { fillPercentage: index <= currentIndex ? 100 : 0, shouldAnimate: false }
      }

      if (index < currentIndex) {
        return { fillPercentage: 100, shouldAnimate: false }
      }

      if (index === currentIndex) {
        return {
          fillPercentage: shouldStartAnimation ? 100 : 0,
          shouldAnimate: isActive && !reset,
        }
      }

      return { fillPercentage: 0, shouldAnimate: false }
    })
  }, [totalCount, isManualMode, currentIndex, isActive, reset, shouldStartAnimation])

  return {
    progressSegments,
    animationKey,
  }
}
